import TopHeaderContainer from '../../components/header/TopHeaderContainer';
import { Grid } from '@mui/material';
import { useTranslation } from '../../contexts/TranslationContext';
import { useEffect, useState } from 'react';
import useApi from '../../api';
import { useResponseHandler } from '../../utils/response';
import {
    PageListItem,
    PageListResponse,
    PageStateBody,
} from '../../api/types/cms';
import PagesColumn, {
    PageContextCallbacks,
} from '../../components/cms/PagesColumn';
import PageLocaleSelect from '../../components/cms/PageLocaleSelect';
import { useDidMountEffect } from '../../utils/mount';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import CmsMoveDialog from '../../components/dialog/CmsMoveDialog';
import CmsCopyDialog from '../../components/dialog/CmsCopyDialog';

export type ColumEntry = {
    locale: string;
    parent: PageListItem | null;
    pages: PageListResponse;
    resetSelection: boolean;
};

type ColumnLocation = {
    parent: PageListItem | null;
    col: number;
};

type PagesProps = {
    overrideLocale?: string;
    withToolbar?: boolean;
    withMoveModal?: boolean;
    withCopyModal?: boolean;
    allowActions?: boolean;
    onPageSelect?: (page: PageListItem) => void;
};

export default function Pages({
    overrideLocale,
    withToolbar = true,
    withMoveModal = true,
    withCopyModal = true,
    allowActions = true,
    onPageSelect = undefined,
}: PagesProps) {
    const navigate = useNavigate();
    const { i18n, translate } = useTranslation();
    const { cms } = useApi();
    const { handleResponse } = useResponseHandler();

    const [currentLocation, setCurrentLocation] = useState<ColumnLocation>({
        parent: null,
        col: 0,
    });
    const [selectedLocale, setSelectedLocale] = useState<string | undefined>();

    const [moveAlert, setMoveAlert] = useState<PageListItem>();
    const [copyAlert, setCopyAlert] = useState<PageListItem>();
    const [columns, setColumns] = useState<Array<ColumEntry>>([]);

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        let locale;
        if (overrideLocale) {
            locale = overrideLocale;
        } else {
            locale = localStorage.getItem('qlive:cms-page-locale') ?? i18n;
        }
        setSelectedLocale(locale);
    }, []);

    const loadChildren = (
        colIndex: number,
        parentPage: PageListItem | null = null,
    ) => {
        if (selectedLocale) {
            cms.getPageList(selectedLocale, parentPage?.id ?? null).then(
                response => {
                    handleResponse(response, () => {
                        if (response) {
                            setColumns(draft => {
                                if (colIndex === 0) {
                                    return [
                                        {
                                            locale: selectedLocale,
                                            parent: parentPage,
                                            pages: response,
                                            resetSelection: false,
                                        },
                                    ];
                                } else {
                                    draft[colIndex] = {
                                        locale: selectedLocale,
                                        parent: parentPage,
                                        pages: response,
                                        resetSelection:
                                            typeof draft[colIndex] !==
                                            'undefined',
                                    };
                                }
                                return draft;
                            });
                            setCurrentLocation({
                                parent: parentPage,
                                col: colIndex,
                            });
                        }
                    });
                },
            );
        }
    };

    useDidMountEffect(() => {
        loadChildren(0);
        localStorage.setItem('qlive:cms-page-locale', selectedLocale ?? i18n);
    }, [selectedLocale]);

    const updateLocale = (locale: string) => {
        setSelectedLocale(locale);
    };

    const stateUpdate = (
        page: PageListItem,
        newState: PageStateBody,
        callback: () => void = () => {},
    ) => {
        cms.updatePageState(page.id, newState).then(response => {
            handleResponse(response, () => {
                enqueueSnackbar(translate('messages.saved'), {
                    variant: 'success',
                });
                callback();
            });
        });
    };

    const deletePage = (page: PageListItem, callback: () => void) => {
        cms.deletePage(page.id).then(response => {
            handleResponse(response, () => {
                enqueueSnackbar(translate('messages.deleted'), {
                    variant: 'success',
                });
                callback();
            });
        });
    };

    const pageContextCallbacks: PageContextCallbacks = {
        edit: page => {
            navigate(`/cms/pages/${page.id}/edit/${selectedLocale}`);
        },
        setHome: (page, columnIndex, parent) =>
            stateUpdate(
                page,
                {
                    online: page.online,
                    home: true,
                },
                () => loadChildren(columnIndex, parent),
            ),
        setOnline: (page, columnIndex, parent) =>
            stateUpdate(
                page,
                {
                    online: true,
                    home: page.home_page,
                },
                () => loadChildren(columnIndex, parent),
            ),
        setOffline: (page, columnIndex, parent) =>
            stateUpdate(
                page,
                {
                    online: false,
                    home: page.home_page,
                },
                () => loadChildren(columnIndex, parent),
            ),
        movePage: setMoveAlert,
        copyPage: setCopyAlert,
        deletePage: (page, columnIndex, parent) =>
            deletePage(page, () => loadChildren(columnIndex, parent)),
        onPageSelect,
    };

    const renderColumn = (column: ColumEntry, columnIndex: number) => {
        return (
            <PagesColumn
                key={`col-${columnIndex}`}
                columnIndex={columnIndex}
                column={column}
                allowActions={allowActions}
                loadChildren={loadChildren}
                callbacks={pageContextCallbacks}
            />
        );
    };

    return (
        <Grid
            container
            columnSpacing={0}
            sx={{ height: 'calc(100% - 180px);' }}>
            {withToolbar && (
                <TopHeaderContainer title={translate('pages.cms.pages.title')}>
                    <PageLocaleSelect
                        selectedLocale={selectedLocale}
                        onLocaleChange={updateLocale}
                    />
                </TopHeaderContainer>
            )}
            {columns.slice(0, currentLocation.col + 1).map((column, index) => {
                return renderColumn(column, index);
            })}
            {withMoveModal && (
                <CmsMoveDialog
                    page={moveAlert}
                    title={translate('pages.cms.pages.move.title')}
                    onClose={() => setMoveAlert(undefined)}
                    onMoved={() => loadChildren(0)}>
                    {translate('pages.cms.pages.move.message')}
                </CmsMoveDialog>
            )}
            {withCopyModal && (
                <CmsCopyDialog
                    page={copyAlert}
                    sourceLocale={selectedLocale}
                    title={translate('pages.cms.pages.copy.title')}
                    onClose={() => setCopyAlert(undefined)}
                    onCopied={() => loadChildren(0)}>
                    {translate('pages.cms.pages.move.message')}
                </CmsCopyDialog>
            )}
        </Grid>
    );
}
